// ============================================
// robson.jsx  —  Assistente flutuante do ZipHub (renderiza HTML)
// ============================================
// Monte UMA vez, dentro do <BrowserRouter> e FORA do <Routes>:
//   <Robson conta={localStorage.idconta} apiUrl="https://node.ziphub.com.br" esconderEm={['/login']} />
// ============================================

import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

export default function Robson({ conta, apiUrl = '', esconderEm = ['/login'] }) {
  const location = useLocation()
  const rotaAtual = location.pathname.toLowerCase()
  const deveEsconder = esconderEm.some((p) => rotaAtual === p.toLowerCase())

  const [aberto, setAberto] = useState(false)
  const [pergunta, setPergunta] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [mensagens, setMensagens] = useState([
    {
      de: 'bot',
      html: 'Oi, eu sou o <b>Robson</b>! 👋 Posso consultar seus dados. Ex: <i>"qual o último pedido?"</i>'
    }
  ])

  const fimRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [mensagens, aberto])

  useEffect(() => {
    if (aberto) inputRef.current?.focus()
  }, [aberto])

  async function enviar() {
    const texto = pergunta.trim()
    if (!texto || carregando) return

    // mensagem do usuário entra como texto puro (escapado)
    setMensagens((m) => [...m, { de: 'user', texto }])
    setPergunta('')
    setCarregando(true)

    try {
      const resp = await fetch(`${apiUrl}/api/ia/chat/${conta}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pergunta: texto })
      })
      const data = await resp.json()

      setMensagens((m) => [
        ...m,
        { de: 'bot', html: data.resposta || `<p>${data.error || 'Sem resposta.'}</p>` }
      ])
    } catch (e) {
      setMensagens((m) => [
        ...m,
        { de: 'bot', html: '<p>Ops, não consegui falar com o servidor.</p>' }
      ])
    } finally {
      setCarregando(false)
    }
  }

  function onKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      enviar()
    }
  }

  if (deveEsconder) return null

  return (
    <>
      {/* CSS pra deixar o HTML das respostas bonito (tabelas, listas, etc) */}
      <style>{cssRespostas}</style>

      <button
        onClick={() => setAberto((v) => !v)}
        style={estilos.botao}
        aria-label="Abrir assistente Robson"
        title="Falar com o Robson"
      >
        {aberto ? '✕' : '🤖'}
      </button>

      {aberto && (
        <div style={estilos.janela}>
          <div style={estilos.header}>
            <span style={estilos.avatar}>🤖</span>
            <div>
              <div style={estilos.nome}>Robson</div>
              <div style={estilos.sub}>Assistente ZipHub</div>
            </div>
          </div>

          <div style={estilos.corpo}>
            {mensagens.map((msg, i) =>
              msg.de === 'user' ? (
                <div key={i} style={{ ...estilos.balao, ...estilos.balaoUser }}>
                  {msg.texto}
                </div>
              ) : (
                <div
                  key={i}
                  className="robson-html"
                  style={{ ...estilos.balao, ...estilos.balaoBot }}
                  dangerouslySetInnerHTML={{ __html: msg.html }}
                />
              )
            )}

            {carregando && (
              <div style={{ ...estilos.balao, ...estilos.balaoBot, ...estilos.digitando }}>
                Robson está consultando…
              </div>
            )}
            <div ref={fimRef} />
          </div>

          <div style={estilos.rodape}>
            <input
              ref={inputRef}
              style={estilos.input}
              value={pergunta}
              onChange={(e) => setPergunta(e.target.value)}
              onKeyDown={onKey}
              placeholder="Pergunte ao Robson…"
              disabled={carregando}
            />
            <button
              style={{ ...estilos.enviar, opacity: carregando ? 0.5 : 1 }}
              onClick={enviar}
              disabled={carregando}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  )
}

// CSS aplicado só dentro dos balões do bot (.robson-html)
const cssRespostas = `
.robson-html { overflow-x: auto; max-width: 100%; }
.robson-html p { margin: 0 0 6px; }
.robson-html b { color: #1e3a8a; }
.robson-html ul { margin: 4px 0; padding-left: 18px; }
.robson-html li { margin: 2px 0; }
.robson-html table {
  border-collapse: collapse; margin: 6px 0; font-size: 12.5px;
  width: auto;               /* não força 100%: usa o tamanho do conteúdo */
}
.robson-html th, .robson-html td {
  border: 1px solid #cbd5e1; padding: 5px 8px; text-align: left;
  white-space: nowrap;       /* impede a quebra letra a letra */
}
.robson-html thead th {
  background: #2563eb; color: #fff; font-weight: 600;
}
.robson-html tbody tr:nth-child(even) { background: #f1f5f9; }
`

const estilos = {
  botao: {
    position: 'fixed', bottom: 24, right: 24, width: 60, height: 60,
    borderRadius: '50%', border: 'none', background: '#2563eb', color: '#fff',
    fontSize: 26, cursor: 'pointer', boxShadow: '0 4px 16px rgba(37,99,235,.4)',
    zIndex: 99999
  },
  janela: {
    position: 'fixed', bottom: 96, right: 24, width: 380, height: 540,
    maxHeight: 'calc(100vh - 120px)', background: '#fff', borderRadius: 16,
    boxShadow: '0 12px 40px rgba(0,0,0,.28)', display: 'flex',
    flexDirection: 'column', overflow: 'hidden', zIndex: 99999,
    fontFamily: 'system-ui, sans-serif'
  },
  header: {
    background: '#2563eb', color: '#fff', padding: '12px 16px',
    display: 'flex', alignItems: 'center', gap: 10
  },
  avatar: { fontSize: 26, lineHeight: 1 },
  nome: { fontWeight: 700, fontSize: 15 },
  sub: { fontSize: 11, opacity: 0.85 },
  corpo: {
    flex: 1, padding: 12, overflowY: 'auto', display: 'flex',
    flexDirection: 'column', gap: 8, background: '#f8fafc'
  },
  balao: {
    maxWidth: '90%', padding: '9px 13px', borderRadius: 14, fontSize: 14,
    lineHeight: 1.45, wordBreak: 'break-word'
  },
  balaoUser: {
    alignSelf: 'flex-end', background: '#2563eb', color: '#fff',
    borderBottomRightRadius: 3, whiteSpace: 'pre-wrap'
  },
  balaoBot: {
    alignSelf: 'stretch', width: '100%', background: '#fff', color: '#0f172a',
    border: '1px solid #e2e8f0', borderBottomLeftRadius: 3,
    boxSizing: 'border-box', overflow: 'hidden'
  },
  digitando: { fontStyle: 'italic', opacity: 0.75 },
  rodape: {
    display: 'flex', gap: 8, padding: 10, borderTop: '1px solid #e2e8f0', background: '#fff'
  },
  input: {
    flex: 1, border: '1px solid #cbd5e1', borderRadius: 10, padding: '9px 12px',
    fontSize: 14, outline: 'none'
  },
  enviar: {
    border: 'none', background: '#2563eb', color: '#fff', borderRadius: 10,
    padding: '0 16px', cursor: 'pointer', fontSize: 16
  }
}